import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import {
  DashboardContentOptions,
  DashboardFrame,
  FrameOptions,
  Parameter,
  SimpleMessageEvent,
  VisualAction,
} from 'amazon-quicksight-embedding-sdk/dist/types';
import { EventName, ReportingCallReportEvents } from 'shared/providers/TrackingProvider/EventNames';

export interface Dict {
  [key: string]: string | number | boolean | undefined;
}

const ActionStatus = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
};

export const QUICKSIGHT_DASHBOARD_IDS = {
  Call_History: '6b47e0e1-f096-42db-93e9-c7bbfa4911f9',
  Clinic_Performance: 'b412d576-cfb3-4a64-b65f-7519537548e9',
  CSAT_Post_Appointment_Survey: '81e296e8-ec1b-41ff-8633-3ced43c414d2',
  NPS_Post_Appointment_Survey: 'b1b3199c-109b-4f0a-93c5-988cbede035f',
};

export const QUICKSIGHT_DASHBOARD_URL = {
  Call_History: 'call-history',
  Clinic_Performance: 'clinic-performance',
  CSAT_Post_Appointment_Survey: 'csat-post-appointment-survey',
  NPS_Post_Appointment_Survey: 'nps-post-appointment-survey',
  Care_Financial_Report: 'care-financial-report',
  LapsedPetParentDashboard: 'lapsed-pet-parent-dashboard',
  EmailCampaigns: 'email-campaigns',
};

const myAddVisualActionOnFirstVisualOfFirstSheet = async (embeddedDashboard: DashboardFrame): Promise<void> => {
  // 1. List the sheets on the dashboard
  // const { SheetId } = (await embeddedDashboard.getSheets())[0];
  // If you'd like to add action on the current sheet instead, you can use getSelectedSheetId method
  const SheetId = await embeddedDashboard.getSelectedSheetId();

  // 2. List the visuals on the specified sheet
  const { VisualId } = (await embeddedDashboard.getSheetVisuals(SheetId))[0];

  // 3. Add the custom action to the visual
  try {
    const customActionId = 'data_point_click';
    const actionName = 'Data Point Click';
    const actionTrigger = ActionTrigger.DATA_POINT_CLICK;
    const actionStatus = ActionStatus.ENABLED;
    const myCustomAction = myConstructCustomActionModel(customActionId, actionName, actionTrigger, actionStatus);
    const response = await embeddedDashboard.addVisualActions(SheetId, VisualId, [myCustomAction]);
    if (!response.success) {
      console.log('Adding visual action failed', response.errorCode);
    }
  } catch (error) {
    console.log('Adding visual action failed', error);
  }
};

const ActionTrigger = {
  DATA_POINT_CLICK: 'DATA_POINT_CLICK',
  DATA_POINT_MENU: 'DATA_POINT_MENU',
};

const myConstructCustomActionModel = (
  customActionId: string,
  actionName: string,
  actionTrigger: string,
  actionStatus: string,
): VisualAction => {
  return {
    Name: actionName,
    CustomActionId: customActionId,
    Status: actionStatus,
    Trigger: actionTrigger,
    ActionOperations: [
      {
        CallbackOperation: {
          EmbeddingMessage: {},
        },
      },
    ],
  } as unknown as VisualAction;
};

const embedDashboard = async (
  embedUrl: string,
  parameters: Parameter[],
  setDownloadUrlAndFileName: React.Dispatch<
    React.SetStateAction<{
      downloadUrl: string;
      fileName: string;
    }>
  >,
  setSelectedPetParentId: React.Dispatch<React.SetStateAction<string>>,
  onTranscriptSummaryModalOpen: () => void,
  setCallHistoryId: React.Dispatch<React.SetStateAction<string>>,
  trackEvent: (name: EventName, params?: Dict) => void,
): Promise<DashboardFrame> => {
  const embeddingContext = await createEmbeddingContext({
    onChange: (changeEvent, metadata) => {
      console.log('Context received a change', changeEvent, metadata);
    },
  });

  const frameOptions: FrameOptions = {
    url: embedUrl,
    container: window.quickSightContainer,
    height: 'AutoFit',
    resizeHeightOnSizeChangedEvent: true,
    onChange: (changeEvent, metadata): void => {
      switch (changeEvent.eventName) {
        case 'FRAME_MOUNTED': {
          console.log('Do something when the experience frame is mounted.');
          break;
        }
        case 'FRAME_LOADED': {
          console.log('Do something when the experience frame is loaded.');
          break;
        }
      }
      console.log('metadata', metadata);
    },
  };

  const contentOptions: DashboardContentOptions = {
    parameters: parameters,
    locale: 'en-US',
    toolbarOptions: {
      export: { print: true },
      undoRedo: true,
      reset: true,
      bookmarks: true,
    },
    attributionOptions: {
      overlayContent: false,
    },
    sheetOptions: {
      emitSizeChangedEventOnSheetChange: true,
    },
    onMessage: async (messageEvent): Promise<void> => {
      switch (messageEvent.eventName) {
        case 'CONTENT_LOADED': {
          console.log('All visuals are loaded. The title of the document:', messageEvent.message.title);
          await myAddVisualActionOnFirstVisualOfFirstSheet(embeddedDashboardExperience);
          break;
        }
        case 'ERROR_OCCURRED': {
          console.log('Error occurred while rendering the experience. Error code:', messageEvent.message.errorCode);
          break;
        }
        case 'PARAMETERS_CHANGED': {
          console.log('Parameters changed. Changed parameters:', messageEvent.message.changedParameters);
          break;
        }
        case 'SELECTED_SHEET_CHANGED': {
          console.log('Selected sheet changed. Selected sheet:', messageEvent.message.selectedSheet);
          break;
        }
        case 'SIZE_CHANGED': {
          console.log('Size changed. New dimensions:', messageEvent.message);
          break;
        }
        case 'MODAL_OPENED': {
          window.scrollTo({
            top: 0, // iframe top position
          });
          break;
        }
        case 'CALLBACK_OPERATION_INVOKED': {
          console.log('Callback operation invoked. Operation name:', messageEvent);
          switch (messageEvent.message.DashboardId) {
            case QUICKSIGHT_DASHBOARD_IDS.Call_History:
              handleCallHistoryClick(
                messageEvent,
                setDownloadUrlAndFileName,
                setSelectedPetParentId,
                onTranscriptSummaryModalOpen,
                setCallHistoryId,
                trackEvent,
              );
              break;
          }
          break;
        }
      }
    },
  };
  const embeddedDashboardExperience = await embeddingContext.embedDashboard(frameOptions, contentOptions);
  return embeddedDashboardExperience;
};

const handleCallHistoryClick = (
  messageEvent: SimpleMessageEvent,
  setDownloadUrlAndFileName: React.Dispatch<
    React.SetStateAction<{
      downloadUrl: string;
      fileName: string;
    }>
  >,
  setSelectedPetParentId: React.Dispatch<React.SetStateAction<string>>,
  onTranscriptSummaryModalOpen: () => void,
  setCallHistoryId: React.Dispatch<React.SetStateAction<string>>,
  trackEvent: (name: EventName, params?: Dict) => void,
): void => {
  const dataPoint: DataPoint = messageEvent.message.Datapoints[0];
  const selectedColumn = dataPoint.SelectedColumnIndex;
  // You might console log these flat lists if you are not sure what the data structure looks like
  const flatColumns =
    dataPoint?.Columns?.map(
      (column) => column?.Measure?.String?.Column?.ColumnName || column?.Measure?.DateTime?.Column?.ColumnName,
    ) || [];
  const flatColumnsValues = dataPoint?.FormattedValues?.map((value) => value?.Value) || [];

  const downloadIndex = flatColumns?.indexOf('Download');
  const downloadURLIndex = flatColumns?.indexOf('call_recording_uri');

  const petParentNameIndex = flatColumns?.indexOf('pet_parent_name');
  const petParentIdIndex = flatColumns?.indexOf('pet_parent_id');
  const timestampIndex = flatColumns?.indexOf('call_created_time');

  const clinicUserNameIndex = flatColumns?.indexOf('user_name');

  const callTypeIndex = flatColumns?.indexOf('call_type');
  const transcriptIndex = flatColumns?.indexOf('transcript_content');

  const downloadUrl = flatColumnsValues[downloadURLIndex] || '';

  const callHistoryIdIndex = flatColumns?.indexOf('call_history_id');
  const callHistoryId = flatColumnsValues[callHistoryIdIndex] || '';

  switch (selectedColumn) {
    case downloadIndex:
      const petParentName = flatColumnsValues[petParentNameIndex] || '';
      const clinicUserName = flatColumnsValues[clinicUserNameIndex] || '';
      const timestamp = flatColumnsValues[timestampIndex] || '';
      const fileName = `Recording of ${petParentName} and ${clinicUserName} from ${timestamp}`;
      if (!!downloadURLIndex) {
        setDownloadUrlAndFileName({ downloadUrl, fileName });
      }
      break;
    case petParentNameIndex:
      if (!!petParentIdIndex) {
        setSelectedPetParentId(flatColumnsValues[petParentIdIndex] || '');
      }
      break;
    case transcriptIndex:
      if (!!flatColumnsValues[transcriptIndex]) {
        trackEvent(ReportingCallReportEvents.TranscriptClick);
        setCallHistoryId(callHistoryId);
        onTranscriptSummaryModalOpen();
      }
      break;
    case callTypeIndex:
      console.log('Call Type: unknown column', flatColumnsValues[callTypeIndex]);
      break;
  }
};

export default embedDashboard;

interface DataPoint {
  SelectedColumnIndex: number;
  Columns?: {
    Measure?: {
      String?: {
        Column?: {
          ColumnName?: string;
        };
      };
      DateTime?: {
        Column?: {
          ColumnName?: string;
        };
      };
    };
  }[];
  FormattedValues?: {
    Value?: string;
  }[];
}
